<template>
  <div class="tw:bg-s-d-s-dark-background-opacity-100 tw:rounded-radius-100">
    <a-select
      v-model:value="selectedWorkspaceId"
      class="workspace-selector tw:h-[32px] tw:w-[180px] tw:bg-white"
      @change="handleSwitchWorkspace"
      :bordered="false"
      popupClassName="tw:w-[320px] tw:p-0 tw:m-0"
      optionLabelProp="label"
      :dropdownMatchSelectWidth="false"
      aria-label="Select workspace"
    >
      <template #dropdownRender="{ menuNode: menu }">
        <div class="tw:py-12 tw:px-16">
          <InputSearchBaseAntd
            v-model="searchValue"
            placeholder="Search by name"
            allow-clear
            @click.stop
            @mousedown.stop
          >
          </InputSearchBaseAntd>
        </div>
        <v-nodes :vnodes="menu" />
      </template>
      <a-select-option
        v-for="option in filteredOptions"
        :key="option.value"
        :value="option.value"
        :label="option.label"
      >
        <div class="tw:flex tw:gap-4 tw:items-center tw:justify-between">
          <div class="tw:flex tw:gap-4 tw:flex-col">
            <div class="tw:flex tw:gap-4 tw:items-center tw:justify-between">
              <div class="tw:text-3 tw:text-s-d-s-dark-text-default-primary">
                {{ option.label }}
              </div>
            </div>
            <div class="tw:flex tw:gap-8 tw:text-s-d-s-dark-text-default-tertiary tw:text-2">
              <IconUser width="16px" height="16px" class="tw:text-s-d-s-dark-icon-primary-default"/>
              <div>{{ renderWorkspaceOwnder(option.user) }}</div>
            </div>
          </div>
          <IconCheck
            v-if="option.isActive"
            width="16px"
            height="16px"
            class="tw:text-brand-mint-green"
          />
        </div>
      </a-select-option>
      <template #suffixIcon>
        <IconChevronDown width="12" height="12" />
      </template>
      <template #notFoundContent>
        <div class="tw:flex tw:flex-col tw:items-center tw:justify-center">
          <IconEmpty class="tw:w-32 tw:h-32" />
          <div class="tw:mt-8 tw:text-1 tw:text-s-d-s-dark-text-default-tertiary">
            No Data
          </div>
        </div>
      </template>
    </a-select>
  </div>
</template>

<script lang="ts" setup>
import { Select as ASelect } from 'ant-design-vue'
import { useWorkspaceStore } from '@/stores/workspaces'
import { storeToRefs } from 'pinia'
import { computed, defineComponent, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import IconChevronDown from '@/components/icons/IconChevronDown.vue'
import { useAuthStore } from '@/stores/auth'
import type { SharedUser } from '@/types/auth'
import IconUser from './icons/chat-bot/IconUser.vue'
import IconCheck from './icons.new/dem/IconCheck.vue'
import InputSearchBaseAntd from '@/components/base/antd/InputSearchBaseAntd.vue'
import IconEmpty from './icons.new/IconEmpty.vue'

const router = useRouter()
const workspaceStore = useWorkspaceStore()
const authStore = useAuthStore()
const { currentUser } = storeToRefs(authStore)
const { data, selected } = storeToRefs(workspaceStore)

const searchValue = ref('')

const VNodes = defineComponent({
  props: {
    vnodes: {
      type: Object,
      required: true,
    },
  },
  render() {
    return this.vnodes
  },
})

onMounted(() => {
  workspaceStore.ensureData()
})

const renderWorkspaceOwnder = (workspaceOwner: SharedUser) => {
  if (!currentUser.value) return workspaceOwner.name
  if (workspaceOwner.email === currentUser.value?.email) {
    return 'By me'
  }
  return workspaceOwner.name
}

const selectedWorkspaceId = computed({
  get: () => selected.value,
  set: (value) => {
    if (value) {
      workspaceStore.setSelected(value as string)
    }
  },
})

const workspaceOptions = computed(() => {
  return data.value.map((workspace) => {
    const isActive = workspace.id === selected.value
    return {
      label: workspace.name,
      value: workspace.id,
      isActive,
      user: workspace.user,
    }
  })
})

const filteredOptions = computed(() => {
  if (!searchValue.value.trim()) {
    return workspaceOptions.value
  }
  const searchLower = searchValue.value.toLowerCase().trim()
  return workspaceOptions.value.filter((option) => option.label.toLowerCase().includes(searchLower))
})

const handleSwitchWorkspace = (value: unknown) => {
  const workspaceId = value as string | number | (string | number)[] | undefined
  if (!workspaceId || typeof workspaceId !== 'string') return

  workspaceStore.setSelected(workspaceId)
  router.push(`/workspaces/${workspaceId}`)
}
</script>

<style scoped lang="scss">
.workspace-selector {
  :deep(.ant-select-selector) {
    height: 32px !important;
  }
  :deep(.ant-select-selection-item) {
    display: flex !important;
    align-items: center !important;
    gap: 6px !important;

    &::before {
      content: '';
      display: inline-block;
      width: 6px;
      height: 6px;
      background-color: var(--sds-color-icon-brand-green);
      flex-shrink: 0;
    }
  }

  :deep(.ant-select) {
    background-color: var(--sds-color-background-opacity-100) !important;
    border-radius: var(--sds-size-radius-100);
  }

  :deep(.ant-select-selection-item),
  :deep(.ant-select-item-option-content),
  :deep(.ant-input) {
    font-family: Poppins !important;
  }

  :deep(.ant-select-dropdown .ant-select-item-option:not(:first-child)) {
    border-top: 1px solid var(--sds-color-border-default-primary) !important;
  }

  :deep(.ant-select-item) {
    padding: 8px 16px !important;
  }

  // Rotate arrow icon when dropdown is open
  &.ant-select-open {
    :deep(.ant-select-arrow) {
      transform: rotate(180deg) !important;
      transition: transform 0.3s ease !important;
    }
  }

  // Default transition for arrow icon
  :deep(.ant-select-arrow) {
    transition: transform 0.3s ease !important;
    transform-origin: center !important;
  }
}
</style>
